import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Main from "../../components/Main";
import TabelaImportacoesExtrato from "./TabelaImportacoesExtrato";
import MensagemSucesso from "../../components/MensagemSucesso";
import MensagemErro from "../../components/MensagemErro";
import CRUDPageHeader from "../../components/CRUDPageHeader";
import AutoCompleteEmpresaCnpj from "../../components/AutoCompleteEmpresaCnpj";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { idEmpresaFromComboIds } from "../../utils/idEmpresa";
import bancos from "../../utils/banks.json";
import {
  buscarContasBancarias,
  buscarImportacaoExtrato,
  buscarLogsImportacaoExtrato,
  listarTodasImportacoesExtrato,
  pollingTodasImportacoesExtrato,
} from "../../services/api-conciliacao.service";
import {
  enfileirarImportacaoExtrato,
  resolverMensagemErroEnfileiramentoImportacao,
} from "../../services/api-integracao-openai.service";
import styles from "./styles.module.css";
import CelulaSituacaoImportacao from "./CelulaSituacaoImportacao";
import ModalDetalheImportacao from "./ModalDetalheImportacao";
import {
  POLL_MS_IMPORTACAO,
  aplicarFiltroImportacoes,
  formatarCodigoBancoImportacao,
  formatarCnpjImportacao,
  formatarPeriodoImportacao,
  formatarSaldoMonetarioImportacao,
  mesclarImportacoesPolling,
  normalizarLinhas,
  resolverAgenciaImportacao,
  resolverContaImportacao,
  resolverSaldoConciliadoImportacao,
} from "../../utils/importacoesExtrato.util";

const POLL_MS = POLL_MS_IMPORTACAO;
const ACCEPT_FILES = ".pdf,.ofx,application/pdf";

const isArquivoPermitido = (file) => {
  const nome = file.name.toLowerCase();
  return (
    file.type === "application/pdf" ||
    nome.endsWith(".pdf") ||
    nome.endsWith(".ofx")
  );
};

const CelulaTextoEllipsis = ({ children, title }) => {
  const texto =
    children == null || children === "" ? "—" : String(children);
  return (
    <Tooltip title={title ?? texto} placement="top-start" enterDelay={400}>
      <span className={styles.celulaEllipsis}>{texto}</span>
    </Tooltip>
  );
};

const renderSaldoConciliado = (row) => {
  const valor = resolverSaldoConciliadoImportacao(row);
  const texto = formatarSaldoMonetarioImportacao(valor);
  if (row?.fechamento_ok === false) {
    const tooltip =
      row.divergencia != null
        ? `Divergência de ${formatarSaldoMonetarioImportacao(row.divergencia)}`
        : "Fechamento do extrato com divergência";
    return (
      <Tooltip title={tooltip} placement="top">
        <span className={`${styles.saldoDivergente} ${styles.celulaEllipsis}`}>
          {texto}
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={texto} placement="top-start" enterDelay={400}>
      <span className={`${styles.saldoOk} ${styles.celulaEllipsis}`}>{texto}</span>
    </Tooltip>
  );
};

const ImportacoesExtrato = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [filtroTabela, setFiltroTabela] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const [openSucesso, setOpenSucesso] = useState(false);
  const [openErro, setOpenErro] = useState(false);

  const [openImportModal, setOpenImportModal] = useState(false);
  const [modalIdEmpresa, setModalIdEmpresa] = useState(null);
  const [modalBanks, setModalBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [modalBankSelected, setModalBankSelected] = useState("");
  const [modalFiles, setModalFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const [openDetalheModal, setOpenDetalheModal] = useState(false);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const [detalheJob, setDetalheJob] = useState(null);
  const [detalheLogs, setDetalheLogs] = useState([]);

  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const idsAtivosRef = useRef(new Set());

  const handleCloseSucesso = useCallback((_, reason) => {
    if (reason === "clickaway") return;
    setOpenSucesso(false);
  }, []);

  const handleCloseErro = useCallback((_, reason) => {
    if (reason === "clickaway") return;
    setOpenErro(false);
  }, []);

  const ordenarPorCriadoEmDesc = useCallback(
    (lista) =>
      [...lista].sort(
        (a, b) => new Date(b.criado_em || 0) - new Date(a.criado_em || 0)
      ),
    []
  );

  const incorporarJobsNaLista = useCallback(
    (novosJobs) => {
      const jobs = normalizarLinhas(novosJobs);
      if (!jobs.length) return;

      setRows((prev) => {
        const map = new Map(normalizarLinhas(prev).map((r) => [r.id, r]));
        jobs.forEach((j) => {
          if (j?.id != null) map.set(j.id, { ...(map.get(j.id) || {}), ...j });
        });
        return ordenarPorCriadoEmDesc([...map.values()]);
      });

      jobs.forEach((j) => {
        if (
          j?.id != null &&
          (j.status === "pendente" || j.status === "em_execucao")
        ) {
          idsAtivosRef.current.add(j.id);
        }
      });
    },
    [ordenarPorCriadoEmDesc]
  );

  const carregarLista = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listarTodasImportacoesExtrato(100);
      const lista = normalizarLinhas(resp.importacoes);
      setRows(lista);
    } catch (err) {
      if (err.response) {
        setMsg(resolverMensagemErroEnfileiramentoImportacao(err));
        setOpenErro(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarPolling = useCallback(async () => {
    try {
      const resp = await pollingTodasImportacoesExtrato();
      const ativos = normalizarLinhas(resp.importacoes);
      let precisaRefresh = false;

      setRows((prev) => {
        const { merged, idsAtivosAgora, idsPrecisamRefresh } =
          mesclarImportacoesPolling(prev, ativos, idsAtivosRef.current);
        idsAtivosRef.current = idsAtivosAgora;

        if (idsPrecisamRefresh.length > 0) {
          precisaRefresh = true;
          return prev;
        }
        if (ativos.length === 0) return prev;

        return merged;
      });

      if (precisaRefresh) {
        await carregarLista();
      }
    } catch {
      /* polling silencioso */
    }
  }, [carregarLista]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  useEffect(() => {
    pollRef.current = setInterval(carregarPolling, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [carregarPolling]);

  const resetModal = () => {
    setModalIdEmpresa(null);
    setModalBanks([]);
    setLoadingBanks(false);
    setModalBankSelected("");
    setModalFiles([]);
    setDragOver(false);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleOpenImportModal = () => {
    resetModal();
    setOpenImportModal(true);
  };

  const handleCloseImportModal = () => {
    setOpenImportModal(false);
    resetModal();
  };

  const handleListModalBanks = async (empresaId) => {
    if (!empresaId) {
      setModalBanks([]);
      return;
    }
    setLoadingBanks(true);
    try {
      const bancosCliente = await buscarContasBancarias(empresaId);
      if (bancosCliente.contasBancarias?.length > 0) {
        const bancosClienteSet = new Set(
          bancosCliente.contasBancarias.map((conta) =>
            conta.codigo_banco.padStart(3, "0")
          )
        );
        const listaFiltrada = bancos.filter((banco) =>
          bancosClienteSet.has(banco.code.toString().padStart(3, "0"))
        );
        setModalBanks(
          listaFiltrada.map((banco) => ({
            value: banco.code,
            label: banco.name,
          }))
        );
      } else {
        setModalBanks([]);
      }
    } catch (error) {
      console.error(error);
      setModalBanks([]);
    } finally {
      setLoadingBanks(false);
    }
  };

  const handleModalEmpresa = (ids) => {
    const empresaId = idEmpresaFromComboIds(ids);
    setModalIdEmpresa(empresaId);
    setModalBankSelected("");
    setModalFiles([]);
    handleListModalBanks(empresaId);
  };

  const handleChangeModalBank = (event) => {
    setModalBankSelected(event.target.value);
  };

  const adicionarArquivos = (fileList) => {
    const novos = Array.from(fileList || []).filter(isArquivoPermitido);
    if (!novos.length) return;
    setModalFiles((prev) => {
      const nomes = new Set(prev.map((f) => f.name));
      return [...prev, ...novos.filter((f) => !nomes.has(f.name))];
    });
  };

  const handleFileInputChange = (event) => {
    adicionarArquivos(event.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    adicionarArquivos(event.dataTransfer.files);
  };

  const handleConfirmarImportacao = async () => {
    if (!modalIdEmpresa) {
      setMsg("Selecione uma empresa.");
      setOpenErro(true);
      return;
    }
    if (!modalFiles.length) {
      setMsg("Selecione ao menos um arquivo PDF ou OFX.");
      setOpenErro(true);
      return;
    }

    const temPdf = modalFiles.some(
      (f) =>
        f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );
    if (temPdf && !modalBankSelected) {
      setMsg("Selecione o banco para arquivos PDF.");
      setOpenErro(true);
      return;
    }

    setUploading(true);
    try {
      const resp = await enfileirarImportacaoExtrato({
        idEmpresa: modalIdEmpresa,
        files: modalFiles,
        codigoBanco: modalBankSelected
          ? modalBankSelected.toString().slice(-3).padStart(3, "0")
          : undefined,
        origemTela: "pagina_importacoes",
      });
      const n = resp?.jobs?.length || modalFiles.length;
      setMsg(`${n} arquivo(s) enviado(s) para processamento.`);
      setOpenSucesso(true);
      incorporarJobsNaLista(resp?.jobs);
      handleCloseImportModal();
      void carregarLista();
      void carregarPolling();
    } catch (err) {
      setMsg(resolverMensagemErroEnfileiramentoImportacao(err));
      setOpenErro(true);
    } finally {
      setUploading(false);
    }
  };

  const handleIrParaExtrato = useCallback(
    (row) => {
      if (!row?.id_empresa) return;
      navigate("/conciliacao/extratos-bancarios", {
        state: {
          idEmpresa: row.id_empresa,
          cnpj: row.cnpj,
        },
      });
    },
    [navigate]
  );

  const handleAbrirDetalhes = useCallback(async (row) => {
    if (!row?.id || !row?.id_empresa) return;
    setOpenDetalheModal(true);
    setDetalheLoading(true);
    setDetalheJob(row);
    setDetalheLogs([]);
    try {
      const [detalheResp, logsResp] = await Promise.all([
        buscarImportacaoExtrato(row.id_empresa, row.id),
        buscarLogsImportacaoExtrato(row.id_empresa, row.id),
      ]);
      setDetalheJob(detalheResp?.importacao || row);
      setDetalheLogs(logsResp?.logs || []);
    } catch {
      setDetalheJob(row);
      setDetalheLogs([]);
    } finally {
      setDetalheLoading(false);
    }
  }, []);

  const handleFecharDetalhes = () => {
    setOpenDetalheModal(false);
    setDetalheJob(null);
    setDetalheLogs([]);
  };

  const columnsImportacao = useMemo(
    () => [
      {
        id: "cnpj",
        label: "CNPJ",
        field: "cnpj",
        width: "12%",
        render: (row) => (
          <CelulaTextoEllipsis>
            {formatarCnpjImportacao(row?.cnpj)}
          </CelulaTextoEllipsis>
        ),
      },
      {
        id: "razao_social",
        label: "Empresa",
        field: "razao_social",
        width: "23%",
      },
      {
        id: "codigo_banco",
        label: "Banco",
        field: "codigo_banco",
        width: "5%",
        render: (row) => (
          <CelulaTextoEllipsis>
            {formatarCodigoBancoImportacao(row?.codigo_banco)}
          </CelulaTextoEllipsis>
        ),
      },
      {
        id: "agencia",
        label: "Agência",
        field: "agencia",
        width: "7%",
        no_ordering: true,
        render: (row) => (
          <CelulaTextoEllipsis>
            {resolverAgenciaImportacao(row) || "—"}
          </CelulaTextoEllipsis>
        ),
      },
      {
        id: "conta",
        label: "Conta",
        field: "conta",
        width: "10%",
        no_ordering: true,
        render: (row) => (
          <CelulaTextoEllipsis>
            {resolverContaImportacao(row) || "—"}
          </CelulaTextoEllipsis>
        ),
      },
      {
        id: "periodo",
        label: "Data",
        field: "dt_inicio",
        width: "15%",
        no_ordering: true,
        render: (row) => (
          <CelulaTextoEllipsis>
            {formatarPeriodoImportacao(row)}
          </CelulaTextoEllipsis>
        ),
      },
      {
        id: "saldo_conciliado",
        label: "Saldo conciliado",
        field: "saldo_conciliado",
        type: "monetario",
        width: "12%",
        no_ordering: true,
        render: renderSaldoConciliado,
      },
      {
        id: "situacao",
        label: "Situação",
        field: "status",
        width: "8%",
        no_ordering: true,
        render: (row) => <CelulaSituacaoImportacao row={row} />,
      },
      {
        id: "acoes",
        label: "Ações",
        width: "8%",
        no_ordering: true,
        render: (row) => (
          <div className={styles.boxAcoes}>
            <Tooltip title="Detalhes da importação">
              <IconButton
                aria-label="detalhes"
                className={styles.iconeTabela}
                size="small"
                onClick={() => handleAbrirDetalhes(row)}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Abrir extrato bancário desta empresa">
              <IconButton
                aria-label="ir para extrato"
                className={styles.iconeTabela}
                size="small"
                disabled={!row?.id_empresa}
                onClick={() => handleIrParaExtrato(row)}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleAbrirDetalhes, handleIrParaExtrato]
  );

  const handleDebounceChange = useCallback((event) => {
    setFiltroTabela(event.target.value);
  }, []);

  const linhasTabela = useMemo(
    () => aplicarFiltroImportacoes(normalizarLinhas(rows), filtroTabela),
    [rows, filtroTabela]
  );

  return (
    <div>
      <Main />

      <CRUDPageHeader
        headerIcon={<CloudUploadIcon fontSize="30px" />}
        headerTitle="Importações de extrato"
        modalButtonLabel="Importar"
        handleOpenModal={handleOpenImportModal}
        handleDebounceChange={handleDebounceChange}
      />

      <TabelaImportacoesExtrato
        columns={columnsImportacao}
        data={linhasTabela}
        loading={loading}
        disableEdit
        disableDelete
        fixedLayout
      />

      <Dialog
        open={openImportModal}
        onClose={handleCloseImportModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className={styles.iconSearch}>
            <CloudUploadIcon sx={{ marginRight: ".5rem" }} />
            Importar extrato
          </div>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            overflow: "visible",
          }}
        >
          <AutoCompleteEmpresaCnpj
            onChange={handleModalEmpresa}
            embedded
            label="Empresa"
            helperText="Busque por CNPJ ou razão social"
            disablePortal
          />

          <TextField
            id="modal-select-banco"
            select
            label="Banco (obrigatório para PDF)"
            helperText="Escolha um dos bancos cadastrados para esta empresa."
            variant="standard"
            fullWidth
            disabled={!modalIdEmpresa || loadingBanks}
            onChange={handleChangeModalBank}
            value={modalBankSelected}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>
              {loadingBanks
                ? "Carregando bancos..."
                : modalBanks.length
                  ? "Selecione o banco"
                  : "Nenhum banco cadastrado para esta empresa"}
            </MenuItem>
            {modalBanks.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <div>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Arquivos (PDF ou OFX)
            </Typography>
          <div
            className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ""} ${!modalIdEmpresa ? styles.dropZoneDisabled : ""}`}
            onDragOver={modalIdEmpresa ? handleDragOver : undefined}
            onDragLeave={modalIdEmpresa ? handleDragLeave : undefined}
            onDrop={modalIdEmpresa ? handleDrop : undefined}
            onClick={() => modalIdEmpresa && fileInputRef.current?.click()}
            role="button"
            tabIndex={modalIdEmpresa ? 0 : -1}
            onKeyDown={(e) => {
              if (!modalIdEmpresa) return;
              if (e.key === "Enter" || e.key === " ") {
                fileInputRef.current?.click();
              }
            }}
          >
            <CloudUploadIcon className={styles.dropZoneIcon} />
            <Typography variant="body2">
              {modalIdEmpresa
                ? "Clique ou arraste os arquivos aqui"
                : "Selecione uma empresa para importar"}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept={ACCEPT_FILES}
              onChange={handleFileInputChange}
              disabled={!modalIdEmpresa}
            />
          </div>

          {modalFiles.length > 0 && (
            <ul className={styles.fileList}>
              {modalFiles.map((file) => (
                <li key={file.name}>{file.name}</li>
              ))}
            </ul>
          )}
          </div>
        </DialogContent>
        <DialogActions className={styles.btnContainer}>
          <Button
            className={`${styles.btnModal} ${styles.cancelar}`}
            onClick={handleCloseImportModal}
            disabled={uploading}
          >
            Cancelar
          </Button>
          <Button
            className={`${styles.btnModal} ${styles.prosseguir}`}
            onClick={handleConfirmarImportacao}
            disabled={uploading}
          >
            {uploading ? "Enviando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDetalheModal}
        onClose={handleFecharDetalhes}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className={styles.iconSearch}>
            <InfoOutlinedIcon sx={{ marginRight: ".5rem" }} />
            Detalhes da importação
          </div>
        </DialogTitle>
        <DialogContent>
          <ModalDetalheImportacao
            loading={detalheLoading}
            detalheJob={detalheJob}
            detalheLogs={detalheLogs}
          />
        </DialogContent>
        <DialogActions className={styles.btnContainer}>
          {detalheJob?.id_empresa ? (
            <Button
              startIcon={<OpenInNewIcon />}
              className={`${styles.btnModal} ${styles.prosseguir}`}
              onClick={() => {
                handleIrParaExtrato(detalheJob);
                handleFecharDetalhes();
              }}
            >
              Ver extrato bancário
            </Button>
          ) : null}
          <Button
            className={`${styles.btnModal} ${styles.cancelar}`}
            onClick={handleFecharDetalhes}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <MensagemSucesso
        open={openSucesso}
        handleClose={handleCloseSucesso}
        msg={msg}
      />
      <MensagemErro open={openErro} handleClose={handleCloseErro} msg={msg} />
    </div>
  );
};

export default ImportacoesExtrato;
